import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  MenuDivider,
  Tooltip,
  Badge,
  Text,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  useToast,
} from "@chakra-ui/react";
import {
  faChevronLeft,
  faHistory,
  faDownload,
  faUndo,
  faKeyboard,
  faStar,
  faBug,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Document, Packer, Paragraph, HeadingLevel, TextRun } from "docx";
import { saveAs } from "file-saver";
import {
  bucketName_files,
  documentBackupBucketName,
  documentBackupTableName,
  supabaseServiceKey,
  loadConfig,
} from "../components/env";
import { getSupabase, getSupabaseAdmin } from "../components/supabaseClient";
import globalPresenceManager from "../utils/globalPresenceManager";
import AvatarDisplay from "../components/AvatarDisplay";
import VersionHistoryModal from "./VersionHistoryModal";
import CollaborationDebugPanel from "../components/CollaborationDebugPanel";
import {
  tenantAccessLevelLabel,
  tenantAccessLevelColorScheme,
} from "../utils/shareAccess";
import { convertDate } from "../utils/utils";

export default function DocumentNavBar({
  documentTitle,
  path,
  editorRef,
  onUserAvatarClick,
  userColors,
  currentPatientId,
  userRole = "USERS",
  onSaveDoc,
  appointmentVersionInfo = null,
  /** { label, isOutOfDate, isStaleVersion, savedNumber, loading } — last saved version vs current editor */
  documentVersionStatus = null,
  /** Called with (versionName: string) when the user confirms saving a manual version. */
  onCreateVersion = null,
  /** VersioningSystem instance — passed through to VersionHistoryModal for cache access. */
  versioningSystem = null,
  /** Patient display name — helps AvatarDisplay match collaborators whose paths use name tokens. */
  patientName = null,
  /** Visit date (YYYY-MM-DD) — scopes avatars for recurring same-patient visits. */
  scheduleDate = null,
  /** Visit time (HH:MM) — shown next to scheduled date in the menu row. */
  scheduleTime = null,
  /** Viewer / read-only: disable Save Version, Reformat, and version Restore */
  documentReadOnly = false,
  /** Resolved tenant share role for the row (owner/admin/editor/viewer/none). */
  shareRoleLevel = null,
}) {
  const shareRoleLabel = tenantAccessLevelLabel(shareRoleLevel);
  const shareRoleColor = tenantAccessLevelColorScheme(shareRoleLevel);
  const navigate = useNavigate();
  const { id } = useParams();
  const { name, date } = useLocation();
  const toast = useToast();

  // state responsible for tracking current active users on doc
  const [currentUserId, setCurrentUserId] = useState(null);

  // Version history modal state
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] =
    useState(false);

  // Save version modal state
  const [isSaveVersionModalOpen, setIsSaveVersionModalOpen] = useState(false);
  const [versionNameInput, setVersionNameInput] = useState("");
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [isRestoringLatestVersion, setIsRestoringLatestVersion] =
    useState(false);

  // Collaboration debug panel state
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);

  // checks for session in order to later retrieve user
  useEffect(() => {
    const checkSession = async () => {
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Error fetching session:", error);
      } else {
        console.log("Session data:", data);
        if (data.session?.user?.id) {
          setCurrentUserId(data.session.user.id);
        }
      }
    };

    checkSession();
  }, []);

  // Reset user removal flag when DocumentNavBar mounts (user navigates to document page)
  useEffect(() => {
    console.log("🔄 DocumentNavBar mounted - resetting user removal flag");
    globalPresenceManager.resetUserRemovalFlag();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSaveVersion = async () => {
    if (documentReadOnly) return;
    const name = versionNameInput.trim();
    if (!name || !onCreateVersion) return;
    setIsSavingVersion(true);
    try {
      await onCreateVersion(name);
      setIsSaveVersionModalOpen(false);
      setVersionNameInput("");
      toast({
        title: "Version saved",
        description: `"${name}" has been saved to version history.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Failed to save version",
        description: e?.message || "An error occurred.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingVersion(false);
    }
  };

  const handleRestoreLatestVersion = async () => {
    if (documentReadOnly || isRestoringLatestVersion) return;

    if (!path) {
      toast({
        title: "Restore failed",
        description: "Document path is missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsRestoringLatestVersion(true);

    try {
      console.groupCollapsed("🧪 [RestoreLastVersion] Start");
      console.log("input.path:", path);
      console.log("versioningSystem available:", !!versioningSystem);

      // Keep schema/config in sync with version modal behavior before querying.
      if (!supabaseServiceKey) {
        try {
          await loadConfig();
        } catch (_) {
          // no-op: handled by fallback error below
        }
      }

      if (!supabaseServiceKey) {
        throw new Error(
          "Configuration not fully loaded. Please refresh the page.",
        );
      }

      const supabaseAdmin = getSupabaseAdmin();
      // Prefer in-memory cache (already used by VersionHistoryModal), then DB.
      const cachedVersions = versioningSystem?.getVersionsCache();
      if (Array.isArray(cachedVersions)) {
        const sortedCached = [...cachedVersions].sort(
          (a, b) => new Date(b.backup_timestamp) - new Date(a.backup_timestamp),
        );
        const cachedLatestSaved = sortedCached[0] || null;
        const cachedLatestAuto =
          sortedCached.find((v) => v?.backup_type === "auto") || null;
        console.log("cache.count:", cachedVersions.length);
        console.log("cache.latestSaved:", {
          backup_type: cachedLatestSaved?.backup_type || null,
          version_name: cachedLatestSaved?.version_name || null,
          backup_timestamp: cachedLatestSaved?.backup_timestamp || null,
          original_path: cachedLatestSaved?.original_path || null,
          backup_path: cachedLatestSaved?.backup_path || null,
        });
        console.log("cache.latestAuto:", {
          backup_type: cachedLatestAuto?.backup_type || null,
          version_name: cachedLatestAuto?.version_name || null,
          backup_timestamp: cachedLatestAuto?.backup_timestamp || null,
          original_path: cachedLatestAuto?.original_path || null,
          backup_path: cachedLatestAuto?.backup_path || null,
        });
      } else {
        console.log("cache.count: not loaded (null)");
      }

      let latestVersion =
        Array.isArray(cachedVersions) && cachedVersions.length > 0
          ? [...cachedVersions].sort(
              (a, b) =>
                new Date(b.backup_timestamp) - new Date(a.backup_timestamp),
            )[0]
          : null;

      if (!latestVersion) {
        const { data: latestVersions, error: versionsError } =
          await supabaseAdmin
            .from(documentBackupTableName || "document_backups")
            .select("backup_path, backup_timestamp, version_name, backup_type")
            .eq("original_path", path)
            .order("backup_timestamp", { ascending: false })
            .limit(1);

        if (versionsError) throw versionsError;
        console.log("db.rowsForPath.count:", latestVersions?.length || 0);
        if (Array.isArray(latestVersions) && latestVersions.length > 0) {
          const dbLatestSaved = latestVersions[0];
          const dbLatestAuto =
            latestVersions.find((v) => v?.backup_type === "auto") || null;
          console.log("db.latestSaved:", {
            backup_type: dbLatestSaved?.backup_type || null,
            version_name: dbLatestSaved?.version_name || null,
            backup_timestamp: dbLatestSaved?.backup_timestamp || null,
            backup_path: dbLatestSaved?.backup_path || null,
          });
          console.log("db.latestAuto:", {
            backup_type: dbLatestAuto?.backup_type || null,
            version_name: dbLatestAuto?.version_name || null,
            backup_timestamp: dbLatestAuto?.backup_timestamp || null,
            backup_path: dbLatestAuto?.backup_path || null,
          });
        }
        latestVersion = latestVersions?.[0] || null;
      }

      console.log("selected.latestVersion:", {
        backup_type: latestVersion?.backup_type || null,
        version_name: latestVersion?.version_name || null,
        backup_timestamp: latestVersion?.backup_timestamp || null,
        backup_path: latestVersion?.backup_path || null,
      });

      if (!latestVersion?.backup_path) {
        console.warn("No latest version found for restore. path:", path);
        toast({
          title: "No saved versions found",
          description:
            "This document does not have a previous version to restore yet.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      let parsedContent = versioningSystem?.getCachedContent(
        latestVersion.backup_path,
      );

      if (!parsedContent) {
        const { data: backupData, error: backupError } =
          await supabaseAdmin.storage
            .from(documentBackupBucketName)
            .download(latestVersion.backup_path);

        if (backupError) throw backupError;

        const contentText = await backupData.text();
        parsedContent = JSON.parse(contentText);
        versioningSystem?.setCachedContent(
          latestVersion.backup_path,
          parsedContent,
        );
      }

      const delta = parsedContent?.delta || parsedContent?.content;
      if (!delta || !Array.isArray(delta.ops)) {
        throw new Error(
          "Selected version does not contain valid editor content.",
        );
      }

      const editor = editorRef?.current?.getEditor?.(0);
      if (!editor) {
        throw new Error("Editor is not ready yet.");
      }

      editor.setContents(delta, "api");

      if (onSaveDoc) {
        await onSaveDoc();
      }

      toast({
        title: "Version restored",
        description: latestVersion.version_name
          ? `Restored "${latestVersion.version_name}".`
          : "Restored the latest saved version.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error restoring latest version:", error);
      toast({
        title: "Restore failed",
        description:
          error?.message || "Failed to restore the latest saved version.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      console.groupEnd();
      setIsRestoringLatestVersion(false);
    }
  };

  const getHeadingLevel = (tag) => {
    switch (tag) {
      case "h1":
        return HeadingLevel.HEADING_1;
      case "h2":
        return HeadingLevel.HEADING_2;
      case "h3":
        return HeadingLevel.HEADING_3;
      default:
        return HeadingLevel.HEADING_1;
    }
  };

  // Helper function to convert Quill color format to hex
  const quillColorToHex = (color) => {
    if (!color) return undefined;
    // If already hex, return as-is
    if (color.startsWith("#")) return color.toUpperCase();
    // If RGB, convert to hex
    const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (rgbMatch) {
      const [_, r, g, b] = rgbMatch;
      return (
        "#" +
        ((1 << 24) + (parseInt(r) << 16) + (parseInt(g) << 8) + parseInt(b))
          .toString(16)
          .slice(1)
          .toUpperCase()
      );
    }
    // Skip oklch and other unsupported formats (docx library will skip color)
    if (
      color.startsWith("oklch") ||
      color.startsWith("hsl") ||
      color.startsWith("lab")
    ) {
      return undefined;
    }
    // Try browser conversion for other formats
    try {
      const tempDiv = document.createElement("div");
      tempDiv.style.color = color;
      document.body.appendChild(tempDiv);
      const computedColor = window.getComputedStyle(tempDiv).color;
      document.body.removeChild(tempDiv);
      const computedRgbMatch = computedColor.match(
        /^rgba?\((\d+),\s*(\d+),\s*(\d+)/,
      );
      if (computedRgbMatch) {
        const [_, r, g, b] = computedRgbMatch;
        return (
          "#" +
          ((1 << 24) + (parseInt(r) << 16) + (parseInt(g) << 8) + parseInt(b))
            .toString(16)
            .slice(1)
            .toUpperCase()
        );
      }
    } catch (error) {
      // Ignore conversion errors
    }
    return undefined;
  };

  // Helper function to convert Quill background color
  const quillBackgroundToShading = (background) => {
    if (!background || background === "transparent" || background === "#ffffff")
      return undefined;
    const hex = quillColorToHex(background);
    if (hex) {
      return {
        type: "clear",
        fill: hex,
      };
    }
    return undefined;
  };

  // Helper function to get alignment from Quill align attribute
  const getAlignment = (align) => {
    if (!align) return undefined;
    const alignMap = {
      left: "LEFT",
      center: "CENTER",
      right: "RIGHT",
      justify: "JUSTIFIED",
    };
    return alignMap[align.toLowerCase()] || undefined;
  };

  const handleDownload = () => {
    if (!editorRef.current) {
      console.warn("EditorRef is null, cannot download document");
      return;
    }

    const editor = editorRef.current.getEditor(0);
    if (!editor) {
      console.warn("Editor is null, cannot download document");
      return;
    }

    // Get content as delta (Quill's native format)
    const delta = editor.getContents();
    if (!delta || !delta.ops || delta.ops.length === 0) {
      console.warn("No content to export");
      return;
    }

    console.log("Exporting document with delta ops:", delta.ops.length);

    // Process delta ops to create DOCX paragraphs
    const contentParagraphs = [];
    let currentParagraphRuns = [];
    let currentParagraphAttrs = {
      header: null,
      align: null,
      indent: null,
    };

    // Line spacing: 1.15 = 276 twips (240 twips = 1 line, so 1.15 * 240 = 276)
    const lineSpacing = { line: 276, lineRule: "auto" };

    const flushParagraph = () => {
      // Always create a paragraph (even if empty) to preserve document structure
      // Empty paragraphs represent blank lines in the document
      const paragraphOptions = {
        children:
          currentParagraphRuns.length > 0
            ? currentParagraphRuns
            : [new TextRun({ text: "" })],
        spacing: lineSpacing,
      };

      // Add heading if present
      if (currentParagraphAttrs.header) {
        paragraphOptions.heading = getHeadingLevel(
          `h${currentParagraphAttrs.header}`,
        );
      }

      // Add alignment if present - CRITICAL for centered headers like "Workup"
      if (currentParagraphAttrs.align) {
        paragraphOptions.alignment = getAlignment(currentParagraphAttrs.align);
        console.log("✅ Applying alignment to paragraph:", {
          align: currentParagraphAttrs.align,
          converted: paragraphOptions.alignment,
          hasRuns: currentParagraphRuns.length > 0,
          firstRunText:
            currentParagraphRuns[0]?.text?.substring(0, 30) || "empty",
          header: currentParagraphAttrs.header,
        });
      }

      // Add indent if present
      if (currentParagraphAttrs.indent) {
        paragraphOptions.indent = { left: currentParagraphAttrs.indent * 720 }; // Convert em to twips (1em = 720 twips)
      }

      contentParagraphs.push(new Paragraph(paragraphOptions));

      // Reset for next paragraph
      currentParagraphRuns = [];
      currentParagraphAttrs = {
        header: null,
        align: null,
        indent: null,
      };
    };

    // Process each delta operation sequentially
    // Simple approach: text operations add content, newline operations flush paragraphs
    for (let i = 0; i < delta.ops.length; i++) {
      const op = delta.ops[i];
      if (!op.insert) continue;

      const text = typeof op.insert === "string" ? op.insert : "";
      const attrs = op.attributes || {};

      // Handle newline operations - these create paragraph breaks
      if (text === "\n") {
        // Apply attributes from this newline to the current paragraph
        if (attrs.header !== undefined)
          currentParagraphAttrs.header = attrs.header;
        if (attrs.align !== undefined)
          currentParagraphAttrs.align = attrs.align;
        if (attrs.indent !== undefined)
          currentParagraphAttrs.indent = attrs.indent;

        // Debug log for alignment
        if (attrs.align) {
          console.log(
            "Setting alignment from newline:",
            attrs.align,
            "for paragraph with",
            currentParagraphRuns.length,
            "runs",
          );
        }

        // Flush current paragraph (creates paragraph break)
        flushParagraph();
        continue;
      }

      // Handle text operations
      if (text.length > 0) {
        // If text contains newlines (rare in Quill), handle them
        // But in Quill, newlines are typically separate operations, so this is mostly for edge cases
        if (text.includes("\n")) {
          const textParts = text.split("\n");

          for (let partIndex = 0; partIndex < textParts.length; partIndex++) {
            const part = textParts[partIndex];
            const isLastPart = partIndex === textParts.length - 1;
            const textEndsWithNewline = text.endsWith("\n");

            // Add text part (if not empty)
            if (part.length > 0) {
              currentParagraphRuns.push(
                new TextRun({
                  text: part,
                  bold: attrs.bold || false,
                  italics: attrs.italic || false,
                  underline: attrs.underline ? {} : undefined,
                  strike: attrs.strike || false,
                  color: quillColorToHex(attrs.color),
                  shading: quillBackgroundToShading(attrs.background),
                }),
              );
            }

            // If not the last part, there's a newline in the middle - flush paragraph
            if (!isLastPart) {
              // Check next operation for attributes
              const nextOp = delta.ops[i + 1];
              if (nextOp?.insert === "\n") {
                const nextAttrs = nextOp?.attributes || {};
                if (nextAttrs.header !== undefined)
                  currentParagraphAttrs.header = nextAttrs.header;
                if (nextAttrs.align !== undefined)
                  currentParagraphAttrs.align = nextAttrs.align;
                if (nextAttrs.indent !== undefined)
                  currentParagraphAttrs.indent = nextAttrs.indent;
              } else {
                if (attrs.header !== undefined)
                  currentParagraphAttrs.header = attrs.header;
                if (attrs.align !== undefined)
                  currentParagraphAttrs.align = attrs.align;
                if (attrs.indent !== undefined)
                  currentParagraphAttrs.indent = attrs.indent;
              }
              flushParagraph();
            } else if (textEndsWithNewline && part === "") {
              // Text ends with newline and this is the empty trailing part
              // Don't flush here - the next "\n" operation will handle it
              // Just set attributes from next operation if it's a newline
              const nextOp = delta.ops[i + 1];
              if (nextOp?.insert === "\n") {
                const nextAttrs = nextOp?.attributes || {};
                if (nextAttrs.header !== undefined)
                  currentParagraphAttrs.header = nextAttrs.header;
                if (nextAttrs.align !== undefined)
                  currentParagraphAttrs.align = nextAttrs.align;
                if (nextAttrs.indent !== undefined)
                  currentParagraphAttrs.indent = nextAttrs.indent;
              }
              // Don't flush - let the next "\n" operation handle it
            } else {
              // Last part and text doesn't end with newline (or we have content)
              // Look ahead to next operation for attributes
              const nextOp = delta.ops[i + 1];
              if (nextOp?.insert === "\n") {
                const nextAttrs = nextOp?.attributes || {};
                if (nextAttrs.header !== undefined)
                  currentParagraphAttrs.header = nextAttrs.header;
                if (nextAttrs.align !== undefined)
                  currentParagraphAttrs.align = nextAttrs.align;
                if (nextAttrs.indent !== undefined)
                  currentParagraphAttrs.indent = nextAttrs.indent;
              } else {
                if (attrs.header !== undefined)
                  currentParagraphAttrs.header = attrs.header;
                if (attrs.align !== undefined)
                  currentParagraphAttrs.align = attrs.align;
                if (attrs.indent !== undefined)
                  currentParagraphAttrs.indent = attrs.indent;
              }
            }
          }
        } else {
          // Simple text - just add to current paragraph
          currentParagraphRuns.push(
            new TextRun({
              text: text,
              bold: attrs.bold || false,
              italics: attrs.italic || false,
              underline: attrs.underline ? {} : undefined,
              strike: attrs.strike || false,
              color: quillColorToHex(attrs.color),
              shading: quillBackgroundToShading(attrs.background),
            }),
          );

          // Look ahead to next operation - if it's a newline, use its attributes
          const nextOp = delta.ops[i + 1];
          if (nextOp?.insert === "\n") {
            const nextAttrs = nextOp?.attributes || {};
            if (nextAttrs.header !== undefined)
              currentParagraphAttrs.header = nextAttrs.header;
            if (nextAttrs.align !== undefined) {
              currentParagraphAttrs.align = nextAttrs.align;
              console.log(
                "Setting alignment from next newline:",
                nextAttrs.align,
                "for text:",
                text.substring(0, 50),
              );
            }
            if (nextAttrs.indent !== undefined)
              currentParagraphAttrs.indent = nextAttrs.indent;
          } else {
            // Use current operation's paragraph-level attributes
            if (attrs.header !== undefined)
              currentParagraphAttrs.header = attrs.header;
            if (attrs.align !== undefined)
              currentParagraphAttrs.align = attrs.align;
            if (attrs.indent !== undefined)
              currentParagraphAttrs.indent = attrs.indent;
          }
        }
      }
    }

    // Flush any remaining paragraph
    flushParagraph();

    // If no paragraphs were created, create an empty one
    if (contentParagraphs.length === 0) {
      contentParagraphs.push(
        new Paragraph({
          children: [new TextRun({ text: "" })],
          spacing: lineSpacing,
        }),
      );
    }

    console.log(
      "Created",
      contentParagraphs.length,
      "paragraphs for DOCX export",
    );

    // Create document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: contentParagraphs,
        },
      ],
    });

    // Generate and save document
    Packer.toBlob(doc)
      .then((blob) => {
        saveAs(blob, `${documentTitle}.docx`);
        console.log("Document exported successfully");
      })
      .catch((error) => {
        console.error("Error exporting document:", error);
      });
  };

  const handleReformat = () => {
    if (!editorRef.current) {
      console.warn("EditorRef is null, cannot reformat document");
      return;
    }

    const editor = editorRef.current.getEditor(0);
    if (!editor) {
      console.warn("Editor is null, cannot reformat document");
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to reformat this document? This will reset all formatting (colors, sizes, etc.) to match the template while preserving your text content.",
    );

    if (!confirmed) {
      return;
    }

    try {
      // Get the current document type to determine which template to use
      const documentType = getDocumentTypeFromPath(path);

      if (!documentType) {
        console.warn("Could not determine document type for reformatting");
        alert(
          "Could not determine document type. Please ensure the document path contains a valid template identifier (NP, NS, PR, ST, FFP, or FFS).",
        );
        return;
      }

      // Get the template for this document type (with NP fallback)
      const template = getTemplateForDocumentType(documentType);
      if (!template) {
        console.warn(
          "Could not find template for document type:",
          documentType,
        );
        alert(
          `Could not find any template for reformatting. Please ensure templates are properly loaded.`,
        );
        return;
      }

      // Log if we're using a fallback template
      const { templates } = require("../assets/templates/index");
      if (!templates[documentType]) {
        console.log(
          `Using NP template as fallback for ${documentType} document type`,
        );
      }

      // Get current content as delta
      const currentDelta = editor.getContents();
      console.log("Current delta:", currentDelta);
      console.log("Current delta ops count:", currentDelta.ops.length);

      // Build a map of template text to formatting
      // Also track which text is followed by a newline with header formatting
      const templateDelta = template.delta;
      const templateFormattingMap = new Map();
      const templateHeaderMap = new Map(); // Maps text to the header level that follows it

      for (let i = 0; i < templateDelta.ops.length; i++) {
        const op = templateDelta.ops[i];

        if (op.insert && typeof op.insert === "string") {
          const text = op.insert;
          const textForMatching = text.replace(/\n/g, "").trim();

          if (textForMatching.length > 0) {
            // Store text formatting
            templateFormattingMap.set(textForMatching, {
              attributes: op.attributes || {},
              originalText: text,
            });

            // Check if the next operation is a newline with header/align formatting
            // or if it's a space followed by a newline with header/align formatting
            let nextOp = templateDelta.ops[i + 1];
            let lineFormatOp = null;

            // Check if nextOp is a newline (possibly with tabs/spaces after it)
            const isNewline = (op) => {
              return (
                op &&
                typeof op.insert === "string" &&
                op.insert.startsWith("\n")
              );
            };

            if (isNewline(nextOp) && nextOp.attributes) {
              // Direct: text → newline with formatting
              if (nextOp.attributes.header || nextOp.attributes.align) {
                lineFormatOp = nextOp;
              }
            } else if (
              nextOp &&
              typeof nextOp.insert === "string" &&
              nextOp.insert.match(/^\s+$/) &&
              !nextOp.insert.includes("\n")
            ) {
              // Check if whitespace (space/tab without newline) is followed by newline with formatting
              const nextNextOp = templateDelta.ops[i + 2];
              if (isNewline(nextNextOp) && nextNextOp.attributes) {
                if (
                  nextNextOp.attributes.header ||
                  nextNextOp.attributes.align
                ) {
                  lineFormatOp = nextNextOp;
                }
              }
            }

            if (lineFormatOp) {
              // Store all line-level attributes (header, align, etc.)
              const lineAttrs = {};
              if (lineFormatOp.attributes.header)
                lineAttrs.header = lineFormatOp.attributes.header;
              if (lineFormatOp.attributes.align)
                lineAttrs.align = lineFormatOp.attributes.align;
              if (lineFormatOp.attributes.list)
                lineAttrs.list = lineFormatOp.attributes.list;
              if (lineFormatOp.attributes.indent)
                lineAttrs.indent = lineFormatOp.attributes.indent;

              templateHeaderMap.set(textForMatching, lineAttrs);
              console.log(
                `📌 Found line formatting after text: "${textForMatching.substring(0, 50)}"`,
                lineAttrs,
              );
            }
          }
        }
      }

      console.log("Template formatting map size:", templateFormattingMap.size);
      console.log("Template header map size:", templateHeaderMap.size);

      // Build separate arrays for text formatting and line formatting
      const textFormattingOps = [];
      const lineFormattingOps = [];
      let currentIndex = 0;
      let formattingAppliedCount = 0;
      let lastMatchedText = null; // Track the last matched text to apply header to following newline

      // Process each operation in the current document
      for (let i = 0; i < currentDelta.ops.length; i++) {
        const currentOp = currentDelta.ops[i];

        if (currentOp.insert && typeof currentOp.insert === "string") {
          const currentText = currentOp.insert;
          const currentTextForMatching = currentText.replace(/\n/g, "").trim();
          const textLength = currentText.length;

          // Handle newlines - check if previous text should have line formatting applied to this newline
          // This includes '\n', '\n\t', '\n\n', etc.
          if (currentText.startsWith("\n")) {
            if (lastMatchedText && templateHeaderMap.has(lastMatchedText)) {
              const lineAttrs = templateHeaderMap.get(lastMatchedText);
              console.log(
                `🎯 Applying line formatting to newline after "${lastMatchedText.substring(0, 50)}"`,
                lineAttrs,
              );

              lineFormattingOps.push({
                position: currentIndex,
                length: 1, // Apply to first newline only
                attributes: lineAttrs,
              });
            }
            lastMatchedText = null; // Reset after newline
            currentIndex += textLength;
            continue;
          }

          // Skip empty text
          if (currentTextForMatching.length === 0) {
            currentIndex += textLength;
            continue;
          }

          // Try to find matching template text
          let matchedFormatting = null;
          let bestMatchScore = 0;
          let bestMatchText = "";

          // Check for exact or similar matches in template
          for (const [templateText, data] of templateFormattingMap) {
            const similarity = calculateTextSimilarity(
              currentTextForMatching,
              templateText,
            );

            if (similarity > bestMatchScore) {
              bestMatchScore = similarity;
              matchedFormatting = data.attributes;
              bestMatchText = templateText;
            }
          }

          console.log(
            `Matching "${currentTextForMatching.substring(0, 50)}" -> best match: "${bestMatchText.substring(0, 50)}" (score: ${bestMatchScore.toFixed(2)})`,
          );

          // If we found a good match, apply the formatting
          if (matchedFormatting && bestMatchScore > 0.2) {
            console.log(
              `✅ Applying formatting to: "${currentTextForMatching.substring(0, 50)}"`,
              matchedFormatting,
            );

            // Store this as the last matched text for header application
            lastMatchedText = bestMatchText;

            // Separate header attributes from text attributes
            const textAttrs = {};
            const lineAttrs = {};

            Object.keys(matchedFormatting).forEach((key) => {
              if (
                key === "header" ||
                key === "align" ||
                key === "list" ||
                key === "indent"
              ) {
                lineAttrs[key] = matchedFormatting[key];
              } else {
                textAttrs[key] = matchedFormatting[key];
              }
            });

            // Store text formatting
            if (Object.keys(textAttrs).length > 0) {
              textFormattingOps.push({
                position: currentIndex,
                length: textLength,
                attributes: textAttrs,
              });
            }

            // Store line formatting (to be applied to the line containing this text)
            if (Object.keys(lineAttrs).length > 0) {
              lineFormattingOps.push({
                position: currentIndex,
                length: textLength,
                attributes: lineAttrs,
              });
            }

            formattingAppliedCount++;
          } else {
            lastMatchedText = null; // No match, reset
          }

          currentIndex += textLength;
        } else {
          // Non-text insert (like embed), just retain
          lastMatchedText = null;
          currentIndex += 1;
        }
      }

      console.log(`Text formatting operations: ${textFormattingOps.length}`);
      console.log(`Line formatting operations: ${lineFormattingOps.length}`);
      console.log(`Formatting applied to ${formattingAppliedCount} sections`);

      if (formattingAppliedCount === 0) {
        alert(
          "No matching sections found to reformat. The document may not match the template structure.",
        );
        return;
      }

      // Store current selection
      const currentSelection = editor.getSelection();

      console.log("Applying formatting to matched sections only...");

      // Disable the editor temporarily to prevent change events
      const wasEnabled = editor.isEnabled();
      editor.disable();

      // Apply text formatting first (colors, bold, underline, etc.)
      console.log("Applying text formatting...");
      textFormattingOps.forEach((op) => {
        if (op.attributes && Object.keys(op.attributes).length > 0) {
          console.log(
            `Text format at position ${op.position} length ${op.length}:`,
            JSON.stringify(op.attributes),
          );

          // Apply new formatting directly - don't remove existing formatting
          // Just override with the new attributes
          editor.formatText(op.position, op.length, op.attributes, "silent");
        }
      });

      // Apply line formatting (headers) using formatLine
      console.log("Applying line formatting (headers)...");
      lineFormattingOps.forEach((op) => {
        if (op.attributes && Object.keys(op.attributes).length > 0) {
          console.log(
            `Line format at position ${op.position}:`,
            JSON.stringify(op.attributes),
          );

          // formatLine applies to the entire line containing the position
          Object.keys(op.attributes).forEach((key) => {
            const value = op.attributes[key];
            editor.formatLine(op.position, op.length, key, value, "silent");
          });
        }
      });

      // Re-enable the editor
      if (wasEnabled) {
        editor.enable();
      }

      console.log("Formatting complete!");

      // Trigger a save by making a no-op change with 'user' source
      // This ensures Yjs syncs the formatting changes to the database
      setTimeout(() => {
        const length = editor.getLength();
        // Insert and immediately delete a space at the end to trigger save
        editor.insertText(length - 1, " ", "user");
        editor.deleteText(length - 1, 1, "user");

        console.log("Triggered save mechanism");

        // Restore selection
        if (currentSelection) {
          editor.setSelection(
            currentSelection.index,
            currentSelection.length,
            "silent",
          );
        }

        console.log("Document reformatted successfully");
        alert(
          `Document has been reformatted successfully! Applied formatting to ${formattingAppliedCount} sections.`,
        );
      }, 100);
    } catch (error) {
      console.error("Error reformatting document:", error);
      alert("Error reformatting document. Please try again.");
    }
  };

  // Helper function to determine document type from path and title
  const getDocumentTypeFromPath = (path) => {
    if (!path) return null;

    // Extract document type from path (e.g., "NP", "NS", "PR", "ST", "FFP", "FFS")
    const pathParts = path.split("/");
    const filename = pathParts[pathParts.length - 1];

    // Look for document type indicators in the filename
    if (filename.includes("NP") || filename.includes("nurse-practitioner"))
      return "NP";
    if (filename.includes("NS") || filename.includes("nurse-specialist"))
      return "NS";
    if (filename.includes("PR") || filename.includes("progress")) return "PR";
    if (filename.includes("ST") || filename.includes("student")) return "ST";
    if (filename.includes("FFP") || filename.includes("free-form"))
      return "FFP";
    if (filename.includes("FFS") || filename.includes("free-form-specialist"))
      return "FFS";

    // Also check document title for type indicators
    if (documentTitle) {
      const title = documentTitle.toLowerCase();
      if (title.includes("np") || title.includes("nurse practitioner"))
        return "NP";
      if (title.includes("ns") || title.includes("nurse specialist"))
        return "NS";
      if (title.includes("pr") || title.includes("progress")) return "PR";
      if (title.includes("st") || title.includes("student")) return "ST";
      if (title.includes("ffp") || title.includes("free form")) return "FFP";
      if (title.includes("ffs") || title.includes("free form specialist"))
        return "FFS";
    }

    return null;
  };

  // Helper function to get template for document type
  const getTemplateForDocumentType = (docType) => {
    // Import templates from the existing templates module
    const { templates } = require("../assets/templates/index");

    // If the document type has a template, use it
    if (templates[docType]) {
      return templates[docType];
    }

    // Fallback to NP template for document types without their own template (like FFP)
    console.log(
      `No template found for ${docType}, falling back to NP template`,
    );
    return templates["NP"] || null;
  };

  // Helper function to extract template content without formatting
  const extractTemplateContent = (template) => {
    if (!template.delta || !template.delta.ops) return "";

    return template.delta.ops
      .filter((op) => op.insert && typeof op.insert === "string")
      .map((op) => op.insert)
      .join("");
  };

  // Helper function to create reformatted delta using smart text matching
  const createReformattedDelta = (currentDelta, template) => {
    const templateDelta = template.delta;
    const newOps = [];

    // Build a map of template text to formatting (don't trim to preserve structure)
    const templateFormattingMap = new Map();
    templateDelta.ops.forEach((op) => {
      if (op.insert && typeof op.insert === "string") {
        // Store the formatting for each piece of template text
        const text = op.insert;
        const textForMatching = text.replace(/\n/g, "").trim(); // Remove newlines for matching only
        if (textForMatching.length > 0) {
          templateFormattingMap.set(textForMatching, {
            attributes: op.attributes || {},
            originalText: text,
          });
        }
      }
    });

    console.log("Template formatting map size:", templateFormattingMap.size);

    // Process each operation in the current document
    currentDelta.ops.forEach((currentOp) => {
      if (currentOp.insert && typeof currentOp.insert === "string") {
        const currentText = currentOp.insert;
        const currentTextForMatching = currentText.replace(/\n/g, "").trim();

        // Skip empty text
        if (currentTextForMatching.length === 0) {
          newOps.push(currentOp);
          return;
        }

        // Try to find matching template text
        let matchedFormatting = null;
        let bestMatchScore = 0;
        let bestMatchText = "";

        // Check for exact or similar matches in template
        for (const [templateText, data] of templateFormattingMap) {
          const similarity = calculateTextSimilarity(
            currentTextForMatching,
            templateText,
          );

          if (similarity > bestMatchScore) {
            bestMatchScore = similarity;
            matchedFormatting = data.attributes;
            bestMatchText = templateText;
          }
        }

        console.log(
          `Matching "${currentTextForMatching.substring(0, 50)}" -> best match: "${bestMatchText.substring(0, 50)}" (score: ${bestMatchScore.toFixed(2)})`,
        );

        // If we found a good match (lowered threshold to 0.2 for better matching)
        if (matchedFormatting && bestMatchScore > 0.2) {
          console.log(
            `✅ Applying formatting to: "${currentTextForMatching.substring(0, 50)}"`,
            matchedFormatting,
          );
          // Only add attributes if there are any, otherwise just add the insert
          if (Object.keys(matchedFormatting).length > 0) {
            newOps.push({
              insert: currentText,
              attributes: matchedFormatting,
            });
          } else {
            newOps.push({
              insert: currentText,
            });
          }
        } else {
          console.log(
            `❌ No match for: "${currentTextForMatching.substring(0, 50)}"`,
          );
          // Keep original formatting if no good match
          newOps.push(currentOp);
        }
      } else {
        // Keep non-text operations as-is
        newOps.push(currentOp);
      }
    });

    return { ops: newOps };
  };

  // Helper function to calculate text similarity
  const calculateTextSimilarity = (text1, text2) => {
    if (!text1 || !text2) return 0;

    // Normalize texts for comparison
    const normalize = (text) =>
      text
        .toLowerCase()
        .replace(/[^\w\s]/g, "")
        .trim();
    const norm1 = normalize(text1);
    const norm2 = normalize(text2);

    // Exact match
    if (norm1 === norm2) return 1.0;

    // Check if one contains the other
    if (norm1.includes(norm2) || norm2.includes(norm1)) {
      const shorter = norm1.length < norm2.length ? norm1 : norm2;
      const longer = norm1.length < norm2.length ? norm2 : norm1;
      return (shorter.length / longer.length) * 0.9;
    }

    // Check for common words
    const words1 = norm1.split(/\s+/).filter((w) => w.length > 1);
    const words2 = norm2.split(/\s+/).filter((w) => w.length > 1);

    if (words1.length === 0 || words2.length === 0) return 0;

    // Count common words
    const commonWords = words1.filter((word) => words2.includes(word));
    const wordSimilarity =
      commonWords.length / Math.max(words1.length, words2.length);

    // Check for medical section headers (HPI, ROS, etc.)
    const medicalHeaders = [
      "hpi",
      "history of present illness",
      "ros",
      "review of systems",
      "pmh",
      "past medical history",
      "psh",
      "past surgical history",
      "family history",
      "social history",
      "allergies",
      "medications",
      "meds",
      "physical exam",
      "pe",
      "assessment",
      "plan",
      "workup",
      "labs",
      "imaging",
    ];

    const isMedicalHeader1 = medicalHeaders.some((header) =>
      norm1.includes(header),
    );
    const isMedicalHeader2 = medicalHeaders.some((header) =>
      norm2.includes(header),
    );

    if (isMedicalHeader1 && isMedicalHeader2) {
      // Boost similarity for medical headers
      return Math.min(1.0, wordSimilarity + 0.3);
    }

    // Check for template placeholders
    const hasPlaceholder1 = /\[.*?\]|___/.test(text1);
    const hasPlaceholder2 = /\[.*?\]|___/.test(text2);

    if (hasPlaceholder1 && hasPlaceholder2) {
      // Boost similarity for placeholder text
      return Math.min(1.0, wordSimilarity + 0.2);
    }

    return wordSimilarity;
  };

  return (
    <>
      <div className="bg-white shadow-md py-2 flex align-center justify-around">
        <div className="flex ml-10">
          <button onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} className="ml-8 mr-4" />
          </button>
          <HStack spacing={2} align="center" flexWrap="wrap">
            <input
              type="text"
              value={documentTitle}
              readOnly
              className="navbar-title"
              style={{ fontSize: "1.5rem" }}
            />
            {shareRoleLabel && (
              <Tooltip
                label={
                  shareRoleLevel === "owner"
                    ? "You own this row."
                    : shareRoleLevel === "admin"
                      ? "Shared with you: full access (edit, share, delete)."
                      : shareRoleLevel === "editor"
                        ? "Shared with you: can edit the document."
                        : "Shared with you: read-only access."
                }
                placement="bottom"
                hasArrow
              >
                <Badge
                  colorScheme={shareRoleColor}
                  fontSize="xs"
                  variant="solid"
                >
                  {shareRoleLabel}
                </Badge>
              </Tooltip>
            )}
            {documentVersionStatus?.loading && (
              <Badge colorScheme="gray" fontSize="xs" variant="subtle">
                …
              </Badge>
            )}
            {!documentVersionStatus?.loading &&
              documentVersionStatus?.label && (
                <Tooltip
                  label={
                    documentVersionStatus.isStaleVersion
                      ? "Your text is closest to an older saved version, not the latest — not on the current head."
                      : documentVersionStatus.label?.includes("· behind")
                        ? "Your text is closer to an older backup than to the latest saved version."
                        : documentVersionStatus.label?.includes("· edited")
                          ? "Unsaved edits on top of the latest saved version."
                          : documentVersionStatus.isOutOfDate
                            ? "Current text differs from the latest saved snapshot (see Version History)."
                            : "In sync with the latest saved version."
                  }
                  placement="bottom"
                  hasArrow
                >
                  <Badge
                    colorScheme={
                      documentVersionStatus.isStaleVersion
                        ? "red"
                        : documentVersionStatus.isOutOfDate
                          ? "orange"
                          : "gray"
                    }
                    fontSize="xs"
                    variant={
                      documentVersionStatus.isOutOfDate ||
                      documentVersionStatus.isStaleVersion
                        ? "solid"
                        : "subtle"
                    }
                  >
                    {documentVersionStatus.label}
                  </Badge>
                </Tooltip>
              )}
            <Tooltip
              label={
                documentReadOnly
                  ? "Not available with viewer (read-only) access"
                  : "Restore the latest saved version"
              }
              placement="bottom"
              hasArrow
            >
              <Button
                size="xs"
                variant="outline"
                leftIcon={<FontAwesomeIcon icon={faUndo} />}
                onClick={handleRestoreLatestVersion}
                isDisabled={documentReadOnly}
                isLoading={isRestoringLatestVersion}
                loadingText="Restoring"
              >
                Restore last version
              </Button>
            </Tooltip>
          </HStack>
        </div>
        <div className="navbar-user-icons">
          {/* Avatar Display - using AvatarDisplay component for consistency */}
          <AvatarDisplay
            currentPatientId={currentPatientId}
            patientName={patientName}
            scheduleDate={scheduleDate}
            viewerDocumentPath={path}
            onUserAvatarClick={onUserAvatarClick}
            showTypingIndicator={true}
            maxAvatars={5}
            userColors={userColors}
            viewerContext="document"
          />
        </div>
      </div>

      <nav className="bg-white shadow-md px-4 pb-2 flex items-center justify-between">
        <div className="flex items-center space-x-4 text-xs">
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="link"
              colorScheme="black"
            >
              File
            </MenuButton>
            <MenuList>
              <MenuItem>New Document</MenuItem>
              <MenuItem>Open</MenuItem>
              <MenuItem>Save</MenuItem>
              <MenuItem>Share</MenuItem>
              <MenuDivider />
              <MenuItem
                onClick={() => {
                  setIsVersionHistoryModalOpen(true);
                }}
                icon={<FontAwesomeIcon icon={faHistory} />}
              >
                Version History
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (documentReadOnly) return;
                  setVersionNameInput("");
                  setIsSaveVersionModalOpen(true);
                }}
                icon={<FontAwesomeIcon icon={faStar} />}
                isDisabled={!onCreateVersion || documentReadOnly}
              >
                Save Version
              </MenuItem>
              <MenuItem onClick={handleDownload}>Download as Docx</MenuItem>
              <MenuItem
                onClick={handleReformat}
                isDisabled={documentReadOnly}
                title={
                  documentReadOnly
                    ? "Not available with viewer (read-only) access"
                    : undefined
                }
              >
                Reformat Document
              </MenuItem>
              <MenuItem>Print</MenuItem>
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="link"
              colorScheme="black"
            >
              Edit
            </MenuButton>
            <MenuList>
              <MenuItem>Undo</MenuItem>
              <MenuItem>Redo</MenuItem>
              <MenuItem>Cut</MenuItem>
              <MenuItem>Copy</MenuItem>
              <MenuItem>Paste</MenuItem>
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="link"
              colorScheme="black"
            >
              View
            </MenuButton>
            <MenuList>
              <MenuItem>Show Ruler</MenuItem>
              <MenuItem>Show Gridlines</MenuItem>
              <MenuItem>Fullscreen</MenuItem>
            </MenuList>
          </Menu>
        </div>
        {scheduleDate && (
          <Text
            fontSize="xs"
            color="black"
            fontWeight="semibold"
            ml="auto"
            whiteSpace="nowrap"
          >
            Scheduled: {convertDate(scheduleDate)}
            {scheduleTime ? ` @ ${scheduleTime}` : ""}
          </Text>
        )}
      </nav>

      {/* Save Version Modal */}
      <Modal
        isOpen={isSaveVersionModalOpen}
        onClose={() => setIsSaveVersionModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Version</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Give this version a name so you can find it later. Manual versions
              are never automatically deleted.
            </Text>
            <Input
              placeholder="e.g. Before major edits, Final draft…"
              value={versionNameInput}
              onChange={(e) => setVersionNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && versionNameInput.trim()) {
                  e.preventDefault();
                  handleSaveVersion();
                }
              }}
              autoFocus
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={() => setIsSaveVersionModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!versionNameInput.trim()}
              isLoading={isSavingVersion}
              onClick={handleSaveVersion}
            >
              Save Version
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionHistoryModalOpen}
        onClose={() => setIsVersionHistoryModalOpen(false)}
        documentPath={path}
        userRole={userRole}
        appointmentVersionInfo={appointmentVersionInfo}
        versioningSystem={versioningSystem}
        documentReadOnly={documentReadOnly}
        onVersionRestored={async (content) => {
          // Handle version restoration
          if (editorRef.current) {
            const editor = editorRef.current.getEditor(0);
            if (editor && content) {
              editor.setContents(content, "api");
              console.log("✅ Document content restored from version");

              // Trigger save after version restoration
              if (onSaveDoc) {
                console.log("💾 Triggering save after version restoration");
                await onSaveDoc();
              }
            }
          }
        }}
      />

      {/* Collaboration Debug Panel Toggle */}

      {/* Collaboration Debug Panel Modal */}
      <CollaborationDebugPanel
        isOpen={isDebugPanelOpen}
        onClose={() => setIsDebugPanelOpen(false)}
        connectionStatus="connected"
        isConnected={true}
        filePath={path}
        presenceList={[]}
      />
    </>
  );
}
